/**
 * Export delivery: share sheet, clipboard, download.
 * All three must be invoked from a user gesture on WebKit.
 */

export function canShareFiles(): boolean {
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    const f = new File([new Uint8Array([137, 80, 78, 71])], 'x.png', { type: 'image/png' });
    return navigator.canShare({ files: [f] });
  } catch {
    return false;
  }
}

/** Files-only share (adding title/text makes iOS treat it as a text share). */
export async function shareFile(file: File): Promise<'shared' | 'cancelled' | 'unsupported' | 'failed'> {
  if (!canShareFiles()) return 'unsupported';
  try {
    await navigator.share({ files: [file] });
    return 'shared';
  } catch (e) {
    const name = (e as { name?: string })?.name;
    return name === 'AbortError' ? 'cancelled' : 'failed';
  }
}

/** Copy a PNG to the clipboard. Pass a *promise* so WebKit accepts the async encode. */
export async function copyPng(png: Promise<Blob>): Promise<boolean> {
  try {
    if (!('clipboard' in navigator) || typeof ClipboardItem === 'undefined') return false;
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    return true;
  } catch {
    return false;
  }
}

/** Text onto the clipboard (Safari asks for it within the tap: call it straight from one). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function safeFilename(title: string, ext: string): string {
  const base = title
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 80);
  return `${base || 'MapMark'}.${ext}`;
}
